import { Grid, makeStyles } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import LinearProgress from '@material-ui/core/LinearProgress';
import moment from 'moment';
import React, { useRef } from 'react';
import {
  fetchEnd,
  fetchStart,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  showNotification,
  TextInput,
  useTranslate
} from 'react-admin';
import { connect } from 'react-redux';

import ResourceUploadApi from '../../../core/apis/ResourceUploadApi';
import FileField from '../../../core/components/forked/field/FileField';
import DateInput from '../../../core/components/forked/input/DateInput';
import FileInput from '../../../core/components/forked/input/FileInput';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import { getIcError } from '../../../core/providers/utils';
import { FileIcon } from '../../../core/themes/icons/FileIcon';
import { PdfIcon } from '../../../core/themes/icons/PdfIcon';
import { WordIcon } from '../../../core/themes/icons/WordIcon';
import { ExcelIcon } from '../../../core/themes/icons/ExcelIcon';
import { CompressedIcon } from '../../../core/themes/icons/CompressedIcon';
import { isValidDate, formatDate } from '../../../core/utils/index';
import setFieldTouched from 'final-form-set-field-touched';
import FieldTitleWithListPopover from '../../../core/components/inputs/FieldTitleWithListPopover';
import { useFormState, useForm, FormSpy } from 'react-final-form';
import { batchTitleVariables } from '../ResourceUploadLookups';
import toArray from 'lodash/toArray';
import AutocompleteInput from '../../../core/components/forked/input/AutocompleteInput';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';
import CheckBoxInput from '../../../core/components/inputs/CheckBoxInput';

const StyledFileInput = withStyles({
  dropZone: {
    background: '#ffffff',
    border: '1px dashed #E0E0E0',
    height: '22px',
    padding: '4px',
    marginTop: '-4px'
  },
  removeButton: {
    textAlign: 'center',
    padding: '.5rem'
  }
})(FileInput);

const useStyles = makeStyles({
  item: {
    padding: '0 8px'
  },
  inputLabel: {
    width: '100%'
  },
  autocompleteSuggestions: {
    zIndex: 3010,
    backgroundColor: '#ffffff'
  }
});

const useFundLevelCheckBoxStyles = makeStyles(theme => ({
  checkbox: {
    display: 'flex',
    alignItems: 'center'
  },
  labelStyle: {
    color: theme.text.primary,
    fontSize: theme.typography.fontSize
  },
  subLabelStyle: {
    display: 'block',
    paddingLeft: 25,
    color: theme.text.secondary,
    fontSize: theme.typography.fontSize
  }
}));

/**
 * Downlaod existing batch and save file locally
 * @param {*} resourceUploadId batch ID
 */
const downloadResource = async resourceUploadId => {
  const response = await ResourceUploadApi.download(resourceUploadId);
  const url = window.URL.createObjectURL(response.data);
  window.open(url, '_blank', 'noopener,noreferrer');
};

const translateWatermarkInputToRecord = v => {
  if (v === -1) {
    return null;
  }
  return v;
};

const translateWatermarkRecordToInput = v => {
  if (v === null) {
    return -1;
  }
  return v;
};

const WatermarkSelectInput = props => {
  let defaultProfile = {
    id: -1,
    profileName: 'Default Profile'
  };
  let customChoices = [];

  customChoices.push(defaultProfile);
  customChoices = customChoices.concat(props.choices);
  return (
    <SelectInput
      resource={props.resource}
      source={props.source}
      initialValue={props.input.value ? props.input.value : -1}
      onChange={props.onChange}
      label="Watermark Profile"
      choices={customChoices}
      optionText="profileName"
      variant={props.variant}
      parse={translateWatermarkInputToRecord}
      format={translateWatermarkRecordToInput}
      disabled={props.disabled}
    />
  );
};

const getFileIcon = fileType => {
  const fileIconStyle = {
    width: '24px',
    height: '24px',
    paddingRight: '8px'
  };

  switch (fileType) {
    case 'application/pdf':
      return <PdfIcon style={fileIconStyle} />;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/msword':
      return <WordIcon style={fileIconStyle} />;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
      return <ExcelIcon style={fileIconStyle} />;
    case 'application/zip':
    case 'application/octet-stream':
    case 'application/x-zip-compressed':
    case 'multipart/x-zip':
      return <CompressedIcon style={fileIconStyle} />;
    default:
      return <FileIcon style={fileIconStyle} />;
  }
};

const FundLevelCheckBox = ({ handleFundLevelDocumentsChanged }) => {
  const classes = useFundLevelCheckBoxStyles();
  const translate = useTranslate();

  return (
    <>
      <CheckBoxInput
        name="metadata.allInvestors"
        label="Fund Level Document"
        onChange={handleFundLevelDocumentsChanged}
        className={classes.checkbox}
        classes={classes}
      />
      <span className={classes.subLabelStyle}>
        {translate('ra.message.fund_level_document')}
      </span>
    </>
  );
};

/**
 * Document details
 */
const DetailsSummary = ({
  props,
  hasSubcategories,
  subcategories,
  onProfileChange,
  onCategoryChange,
  hasWatermark,
  onSecurityChange,
  variant,
  formApi,
  isFundLevelFlowAvailable,
  handleFundLevelDocumentsChanged,
  isDocumentTagLoading
}) => {
  const profileData = props.resourceupload.profileData;
  const watermarkId = profileData ? profileData.watermarkId : null;
  const isApiUploadedBatch = props.resourceupload.uploadVia === 'API';
  //disable fields for Active batches or uploadedVia API
  const isDisabled =
    props.resourceupload.batchStatus === 'A' || isApiUploadedBatch;
  const classes = useStyles();
  const formState = useFormState();
  const form = useForm();
  const doucumentTitleRef = useRef(null);

  function handleSelectVariableSubject(variable) {
    let currentValue = toArray(formState.values.metadata.documentTitle || '');
    const cursorIndex = doucumentTitleRef.current.selectionStart || 0;
    currentValue.splice(cursorIndex, 0, variable.value);

    form.change('metadata.documentTitle', currentValue.join(''));
  }

  return (
    <Grid container spacing={0}>
      <Grid item md={props.bulkMode ? 4 : 6} xs={12} className={classes.item}>
        {!isDisabled && (
          <StyledFileInput
            source="file"
            label="Document"
            accept={
              props.bulkMode
                ? 'application/zip,application/x-zip,application/x-zip-compressed, ' +
                  'application/pdf'
                : 'application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/plain, application/msword,' +
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.ms-powerpoint,' +
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation,text/xml, ' +
                  'application/zip,application/x-zip,application/x-zip-compressed,audio/mp4,video/mp4,application/ogg,video/ogg,audio/mp3, ' +
                  'image/bmp,image/gif,image/jpeg,image/png,image/tiff,video/x-flv,video/quicktime,audio/mp3,audio/mpeg,audio/wav,audio/x-wav, video/x-ms-wmv, video/webm,' +
                  '.tsv, .xlsm, .xlsb, .rtf, .pptm, .pptb, .ott, .ogg, .odt, .ods, .docm, .docb, .csv, .cer, .avi, .flv, .webm, .wmv'
            }
            placeholder="Drag Files Here or Browse"
            resource={props.resource}
            validate={
              props.resourceupload.resourceUploadId ? null : [required()]
            }
            variant={variant}
          >
            <FileField
              source="src"
              title="title"
              size="size"
              type="type"
              target="_blank"
            />
          </StyledFileInput>
        )}
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData &&
            !formData.file &&
            props.resourceupload.resourceUploadId && (
              <Grid item xs={12} style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="link"
                  onClick={() =>
                    downloadResource(props.resourceupload.resourceUploadId)
                  }
                >
                  {getFileIcon(props.resourceupload.fileType)}
                  <span style={{ color: '#2962FF' }}>
                    {props.resourceupload.fileName || 'Unknown File Name'}
                  </span>
                </button>
              </Grid>
            )
          }
        </FormDataConsumer>
      </Grid>
      <Grid item md={props.bulkMode ? 4 : 6} xs={12} className={classes.item}>
        <ReferenceInput
          allowEmpty
          label="Document Profile"
          source="metadata.documentProfileId"
          reference="documentpresets"
          resource={props.resource}
          filter={{ uploadType: props.bulkMode ? 'BULK' : 'SINGLE' }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={-1}
          onChange={value => {
            onProfileChange(value, props);
          }}
        >
          <AutocompleteInput
            optionText="presetName"
            clientSideFilterOnly
            variant={variant}
            disabled={isDisabled}
            options={{
              suggestionsContainerProps: {
                className: classes.autocompleteSuggestions
              }
            }}
          />
        </ReferenceInput>
      </Grid>
      {props.bulkMode && (
        <Grid item md={4} xs={12} className={classes.item}>
          <ReferenceInput
            label="Split Method"
            source="metadata.profileData.splitMethodId"
            reference="splitmethods"
            resource={props.resource}
          >
            <SelectInput
              optionText="name"
              variant={variant}
              validate={isApiUploadedBatch ? undefined : required()}
              disabled={isDisabled}
            />
          </ReferenceInput>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <TextInput
          source="metadata.documentTitle"
          resource={props.resource}
          validate={isApiUploadedBatch ? undefined : required()}
          inputRef={doucumentTitleRef}
          options={{
            autoComplete: 'off',
            label: (
              <FieldTitleWithListPopover
                source="Document"
                label="Document Title"
                onSelectItem={handleSelectVariableSubject}
                items={batchTitleVariables}
                displayingVariables={!isDisabled}
                isRequired={!isApiUploadedBatch}
              />
            )
          }}
          InputLabelProps={{
            classes: {
              root: classes.inputLabel
            }
          }}
          variant={variant}
          disabled={isDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <ReferenceInput
          label="Security"
          source="metadata.profileData.documentSecurityId"
          reference="documentsecurities"
          resource={props.resource}
          onChange={event => {
            onSecurityChange(event.target.value);
          }}
        >
          <SelectInput
            optionText="description"
            variant={variant}
            validate={isApiUploadedBatch ? undefined : required()}
            disabled={isDisabled}
          />
        </ReferenceInput>
      </Grid>
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <ReferenceInput
          label="Document Type"
          source="metadata.documentTypeId"
          reference="documenttypes"
          resource={props.resource}
          sort={{ field: 'description', order: 'ASC' }}
          perPage={-1}
        >
          <AutocompleteInput
            optionText="description"
            clientSideFilterOnly
            variant={variant}
            validate={
              isApiUploadedBatch
                ? undefined
                : [required(), validateDocumentType]
            }
            disabled={isDisabled}
            options={{
              suggestionsContainerProps: {
                className: classes.autocompleteSuggestions
              }
            }}
          />
        </ReferenceInput>
      </Grid>
      {hasWatermark ? (
        <Grid item xs={12} sm={12} md={6} className={classes.item}>
          <ReferenceInput
            source="metadata.profileData.watermarkId"
            reference="documentwatermarks"
            resource={props.resource}
            sort={{ field: 'profileName', order: 'ASC' }}
            perPage={-1}
            initialValue={watermarkId}
          >
            <WatermarkSelectInput variant={variant} disabled={isDisabled} />
          </ReferenceInput>
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={6} className={classes.item}></Grid>
      )}
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <ReferenceInput
          label="Category"
          source="metadata.profileData.categoryId"
          reference="categories"
          resource={props.resource}
          filter={{ subType: 'REPORTING', status: 'ACTIVE' }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={-1}
          onChange={value => {
            onCategoryChange(value, true);
          }}
        >
          <AutocompleteInput
            optionText="name"
            clientSideFilterOnly
            variant={variant}
            validate={isApiUploadedBatch ? undefined : required()}
            disabled={isDisabled}
            options={{
              suggestionsContainerProps: {
                className: classes.autocompleteSuggestions
              }
            }}
          />
        </ReferenceInput>
      </Grid>
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <DateInput
          label="Document Date"
          source="metadata.documentDate"
          resource={props.resource}
          validate={isApiUploadedBatch ? undefined : [required(), isValidDate]}
          placeholder="MM/dd/yyyy"
          customParseValue={formatDate}
          disabled={isDisabled}
          customOnChange={() => {
            //set Due Date touched to true so validation error is displayed if any
            formApi.mutators.setFieldTouched('metadata.dueDate', true);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData &&
            hasSubcategories && (
              <SelectInput
                source="metadata.profileData.subcategoryId"
                label="Subcategory"
                optionText="name"
                choices={subcategories}
                allowEmpty
                variant={variant}
                disabled={isDisabled}
              />
            )
          }
        </FormDataConsumer>
      </Grid>
      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        <DateInput
          label="Due Date"
          source="metadata.dueDate"
          resource={props.resource}
          placeholder="MM/dd/yyyy"
          validate={[isValidDate]}
          customParseValue={formatDate}
          disabled={isDisabled}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6} className={classes.item}>
        {isDocumentTagLoading && (
          <>
            <LinearProgress style={{ width: '300px' }} />
          </>
        )}
        {!props.bulkMode && isFundLevelFlowAvailable ? (
          <>
            <FundLevelCheckBox
              handleFundLevelDocumentsChanged={handleFundLevelDocumentsChanged}
            />
          </>
        ) : null}
      </Grid>
    </Grid>
  );
};

/**
 * Forbid 0 as value for document type
 * @param {*} value
 */
const validateDocumentType = value => {
  if (value === 0) {
    return 'Required';
  }
};

/**
 * Document details form validation rules
 * @param {*} values Form values
 * @param resourceupload
 * @param bulkMode
 * @param hasWatermark
 * @param resourceupload
 * @param bulkMode
 * @param hasWatermark
 * @param resourceupload
 * @param bulkMode
 * @param hasWatermark
 * @returns errors object
 */
const validateDocumentDetails = (
  values,
  resourceupload,
  bulkMode,
  hasWatermark
) => {
  const errors = { metadata: { profileData: {} } };
  if (values.metadata.documentDate && values.metadata.dueDate) {
    if (
      moment(values.metadata.documentDate).startOf('day') >
      moment(values.metadata.dueDate).startOf('day')
    ) {
      errors.metadata.dueDate = 'Due Date must be after Document Date';
    }
  }
  if (hasWatermark && !bulkMode) {
    if (
      (resourceupload &&
        resourceupload.resourceUploadId &&
        resourceupload.fileType !== 'application/pdf') ||
      (values && values.file && values.file.type !== 'application/pdf')
    ) {
      errors.metadata.profileData.documentSecurityId =
        'Watermark can not be applied to non-PDF documents';
    }
  }
  return errors;
};

/**
 * Document Details step
 */
class DetailsStep extends React.Component {
  formApi;
  pristine;

  state = {
    hasSubcategories: false,
    subcategories: undefined,
    uploading: false,
    hasWatermark: false
  };

  componentDidMount() {
    const { resourceupload } = this.props;
    resourceupload &&
      resourceupload.profileData &&
      resourceupload.profileData.categoryId &&
      this.onCategoryChange(resourceupload.profileData.categoryId);
    resourceupload &&
      resourceupload.profileData &&
      resourceupload.profileData.watermarkId &&
      this.setState({ hasWatermark: true });
    resourceupload &&
      resourceupload.profileData &&
      resourceupload.profileData.documentSecurityId &&
      this.onSecurityChange(resourceupload.profileData.documentSecurityId);
  }

  onProfileChange = profileId => {
    if (profileId) {
      const self = this;
      fetchStart();
      const crudOperation = 'GET_ONE';
      dataProvider(crudOperation, 'documentpresets', { id: profileId })
        .then(({ data }) => {
          if (data) {
            // if there is the default document type we should preselect that
            // if there is not we should select first from the drop down list
            // problem is because we are not supporting ALL TYPES value as on FLEX
            // for more info please refer to TFS-140204. This should be changed in
            // the future and we should use ALL TYPES as a default value
            if (data.documentTypeId && data.documentTypeId !== 0) {
              self.formApi.change(
                'metadata.documentTypeId',
                data.documentTypeId
              );
            } else {
              // fetch all document types and use first from the result as a default one
              this.fetchDocumentTypesAndPreselectFirst(self);
            }
            this.formApi.batch(() => {
              self.formApi.change(
                'metadata.documentTitle',
                data.resourceUploadTitle
              );
              self.formApi.change(
                'metadata.profileData.splitMethodId',
                data.splitMethodId
              );
              self.formApi.change(
                'metadata.profileData.documentSecurityId',
                data.documentSecurityId
              );
              self.formApi.change(
                'metadata.profileData.watermarkId',
                data.watermarkProfileId
              );
              self.formApi.change(
                'metadata.profileData.categoryId',
                data.categoryId
              );
              self.formApi.change(
                'metadata.profileData.subcategoryId',
                data.subCategoryId
              );
            });
            self.formApi.mutators.setFieldTouched(
              'metadata.profileData.documentSecurityId',
              true
            );
            this.onCategoryChange(data.categoryId);
            // onSecurityChange won't be called until onProfileChange is finished, so we have to parse and manipulate watermarkId again
            this.onSecurityChange(
              data.documentSecurityId,
              data.watermarkProfileId
            );
          }
        })
        .catch(error => {
          this.props.showNotification('ra.notification.server', 'error', {
            messageArgs: { message: getIcError(error) }
          });
        })
        .finally(() => {
          // Dispatch an action letting react-admin know a API call has ended
          fetchEnd();
        });
    }
  };

  fetchDocumentTypesAndPreselectFirst = self => {
    const crudOperation = 'GET_LIST';
    dataProvider(crudOperation, 'documenttypes', {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'description', order: 'ASC' }
    })
      .then(({ data }) => {
        if (data && data[0]) {
          self.formApi.change('metadata.documentTypeId', data[0].id);
        }
      })
      .catch(error => {
        this.props.showNotification('ra.notification.server', 'error', {
          messageArgs: { message: getIcError(error) }
        });
      })
      .finally(() => {
        // Dispatch an action letting react-admin know a API call has ended
        fetchEnd();
      });
  };

  onCategoryChange = (categoryId, clearSubcategorry = false) => {
    if (clearSubcategorry) {
      this.formApi.change('metadata.profileData.subcategoryId', null);
    }
    const crudOperation = 'GET_LIST';
    dataProvider(crudOperation, 'subcategories', {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'name', order: 'DESC' },
      filter: { categoryId: categoryId, status: 'ACTIVE' }
    })
      .then(({ data }) => {
        if (data) {
          var subCats = data.filter(subcat => subcat.name !== '');
          if (subCats.length > 0) {
            this.setState(() => {
              return {
                hasSubcategories: true,
                subcategories: subCats
              };
            });
          } else {
            this.setState(() => {
              return {
                hasSubcategories: false,
                subcategories: undefined
              };
            });
          }
        }
      })
      .catch(error => {
        this.props.showNotification('ra.notification.server', 'error', {
          messageArgs: { message: getIcError(error) }
        });
      })
      .finally(() => {
        // Dispatch an action letting react-admin know a API call has ended
        fetchEnd();
      });
  };

  /**
   * Return upload percentage
   */
  uploadPercent = () => {
    const { uploadCurrent, uploadTotal } = this.state;
    if (!isNaN(uploadCurrent) && !isNaN(uploadTotal)) {
      return 100 * (uploadCurrent / uploadTotal);
    }
    return 0;
  };

  onSecurityChange = (securityId, watermarkId) => {
    fetchStart();
    const crudOperation = 'GET_ONE';
    dataProvider(crudOperation, 'documentsecurities', { id: securityId })
      .then(({ data }) => {
        if (data && data.applyWatermark) {
          this.setState(() => {
            return {
              hasWatermark: true
            };
          });
          // if we parse watermarkId, it means that a doc preset with a watermark is being loaded
          watermarkId &&
            this.formApi.change(
              'metadata.profileData.watermarkId',
              watermarkId
            );
        } else {
          this.formApi.change('metadata.profileData.watermarkId', null);
          this.setState(() => {
            return {
              hasWatermark: false
            };
          });
        }
      })
      .catch(error => {
        this.props.showNotification('ra.notification.server', 'error', {
          messageArgs: { message: getIcError(error) }
        });
      })
      .finally(() => {
        // Dispatch an action letting react-admin know a API call has ended
        fetchEnd();
      });
  };

  render() {
    const { hasSubcategories, subcategories, hasWatermark } = this.state;
    const {
      resourceupload,
      bulkMode,
      onFormInit,
      variant,
      isFundLevelFlowAvailable,
      handleFundLevelDocumentsChanged,
      fundLevelDocument,
      isDocumentTagLoading
    } = this.props;

    return (
      <>
        <CustomSimpleForm
          form={this.props.formName}
          save={this.uploadDocument}
          mutators={{ setFieldTouched }}
          toolbar={null}
          initialValues={{
            metadata: {
              documentDate:
                resourceupload.documentDate || moment().format('YYYY-MM-DD'),
              dueDate: resourceupload.dueDate,
              documentTitle: resourceupload.documentTitle,
              documentProfileId: resourceupload.documentProfileId,
              documentTypeId: resourceupload.documentTypeId,
              profileData: resourceupload.profileData || {
                uploadType: bulkMode ? 'BULK' : 'SINGLE'
              },
              allInvestors: fundLevelDocument
            }
          }}
          resource={this.props.resource}
          variant="standard"
          margin="normal"
          validate={values =>
            validateDocumentDetails(
              values,
              resourceupload,
              bulkMode,
              hasWatermark
            )
          }
          //in case state is changed prevent resetting values to initialValues
          //for example if batch had subcategory and we change category with no subcategories
          //when we submit form subcateogry is filled with initial value, same for watermark
          initialValuesEqual={() => true}
        >
          <FormSpy subscription={{ pristine: true }}>
            {formprops => {
              // Store current form API object
              if (this.formApi === undefined && onFormInit !== undefined) {
                onFormInit(formprops.form);
              }
              this.formApi = formprops.form;
              this.pristine = formprops.pristine;

              return (
                <DetailsSummary
                  props={this.props}
                  hasSubcategories={hasSubcategories}
                  subcategories={subcategories}
                  onProfileChange={this.onProfileChange}
                  onCategoryChange={this.onCategoryChange}
                  variant={variant}
                  hasWatermark={hasWatermark}
                  onSecurityChange={this.onSecurityChange}
                  formApi={this.formApi}
                  isFundLevelFlowAvailable={isFundLevelFlowAvailable}
                  handleFundLevelDocumentsChanged={
                    handleFundLevelDocumentsChanged
                  }
                  isDocumentTagLoading={isDocumentTagLoading}
                />
              );
            }}
          </FormSpy>
        </CustomSimpleForm>
        <Dialog open={this.state.uploading}>
          <LinearProgress
            variant="determinate"
            value={this.uploadPercent()}
            style={{ width: '300px', height: '15px' }}
          />
        </Dialog>
      </>
    );
  }

  /**
   * Upload document using resourceupload API
   */
  uploadDocument = async values => {
    var self = this;

    const {
      resourceupload,
      onSubmitEnd,
      handleResetActionButtons,
      disableActionButtons,
      handleUploadChange,
      fundLevelDocument
    } = self.props;
    disableActionButtons();
    // If the form is untouched then do nothing and go to next step
    if (this.pristine) {
      onSubmitEnd(resourceupload);
      return;
    }

    try {
      // Multi-part form data
      const formData = new FormData();
      let uploadedResourceupload;

      if (resourceupload.resourceUploadId) {
        // UPDATE resourceupload
        formData.append('resourceUploadId', resourceupload.resourceUploadId);
        if (values.file) {
          this.setState({ uploading: true });
          formData.append('file', values.file.rawFile, values.file.title);
        }
        formData.append(
          'metadata',
          JSON.stringify({
            ...values.metadata,
            fileName: values.file ? values.file.title : resourceupload.fileName,
            year: moment(values.metadata.documentDate).year(),
            quarter: moment(values.metadata.documentDate).quarter(),
            fileType: values.file && values.file.rawFile.type,
            fundIds: resourceupload.fundIds, // when update get fundIds and fundFamilyId from already existing resourceupload
            fundFamilyId: resourceupload.fundFamilyId,
            allInvestors: fundLevelDocument
          })
        );

        uploadedResourceupload = await ResourceUploadApi.updateResourceUpload(
          formData,
          (loaded, total) => {
            this.setState({ uploadCurrent: loaded, uploadTotal: total });
          }
        );
        //set uploadChanged in resourceUpload level state if file or document profile is changed and there is no investors step
        //if this is true on Funds Step submit new resourceFundInvestors will be created
        if (
          (values.file && resourceupload.allInvestors) ||
          resourceupload.documentProfileId !==
            uploadedResourceupload.documentProfileId
        ) {
          handleUploadChange(true);
        }
      } else {
        this.setState({ uploading: true });

        // CREATE resourceupload
        formData.append('file', values.file.rawFile, values.file.title);
        formData.append(
          'metadata',
          JSON.stringify({
            ...values.metadata,
            fileName: values.file.title,
            year: moment(values.metadata.documentDate).year(),
            quarter: moment(values.metadata.documentDate).quarter(),
            fileType: values.file.rawFile.type,
            allInvestors: fundLevelDocument
          })
        );
        uploadedResourceupload = await ResourceUploadApi.upload(
          formData,
          (loaded, total) => {
            this.setState({ uploadCurrent: loaded, uploadTotal: total });
          }
        );
      }

      if (onSubmitEnd) {
        onSubmitEnd(uploadedResourceupload);
      }
    } catch (error) {
      handleResetActionButtons();
      this.props.showNotification('ra.notification.server', 'error', {
        messageArgs: { message: getIcError(error) }
      });
    } finally {
      this.setState({ uploading: false });
    }
  };
}

const mapDispatchToProps = {
  showNotification
};

export default connect(null, mapDispatchToProps)(DetailsStep);
